import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";

const SHOW_THRESHOLD = 400;

/**
 * Floating "scroll to top" button. Drop it anywhere inside a scrollable
 * screen and it auto-discovers the nearest scrollable ancestor (falling
 * back to the window) — no ref plumbing required from the caller.
 */
export default function ScrollToTopButton() {
  const anchorRef = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<HTMLElement | Window | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let el: HTMLElement | null = anchorRef.current?.parentElement ?? null;
    while (el) {
      const style = getComputedStyle(el);
      const isScrollable =
        (style.overflowY === "auto" || style.overflowY === "scroll") &&
        el.scrollHeight > el.clientHeight;
      if (isScrollable) break;
      el = el.parentElement;
    }
    const target: HTMLElement | Window = el ?? window;
    scrollTargetRef.current = target;

    const getScrollTop = () =>
      target === window ? window.scrollY : (target as HTMLElement).scrollTop;

    const handleScroll = () => setVisible(getScrollTop() > SHOW_THRESHOLD);
    handleScroll();
    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    const target = scrollTargetRef.current;
    if (!target) return;
    if (target === window) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      (target as HTMLElement).scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  return (
    <>
      <div ref={anchorRef} className="hidden" aria-hidden="true" />
      {visible && (
        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Scroll to top"
          className="fixed bottom-6 right-6 z-40 w-10 h-10 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-lg flex items-center justify-center transition-all animate-fade-in"
        >
          <ArrowUp size={18} />
        </button>
      )}
    </>
  );
}
